/**
 * Custom React hook that tracks and returns the current URL (including hash).
 *
 * This hook listens for:
 * - 'popstate' events (browser back/forward navigation)
 * - 'hashchange' events (changes to the browser URL)
 * - calls to 'history.pushState' and 'history.replaceState'
 *
 * Whenever the URL changes through any of the above mechanisms, the hook updates it's internal state so components using this hook will rerender with latest URL
 *
 * @example
 * const currentUrl=useCurrentUrl() //https:www.example/com#section
 * console.log(currentUrl) //https:www.example/com#section
 *
 * @returns {string} the current full URL including hash
 *
 */
import { useEffect, useState } from 'react';

export const useCurrentUrl = () => {
  const [url, setUrl] = useState(() => (typeof window !== 'undefined' ? window.location.href : ''));

  useEffect(() => {
    const handleUrlChange = () => setUrl(window.location.href);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    //to detect URL changes on pushState
    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleUrlChange();
    };

    //to detect URL changes on replaceState
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleUrlChange();
    };

    //listen for browser navigation and hashchange
    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);

    //cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, []);

  return url;
};
